import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";
import { CookiePreferencesLink } from "@/components/CookieConsentBanner";
import { generatePageTitle, generateCanonicalUrl } from "@/lib/seoConfig";

const CookiePolicy = () => {
  return (
    <>
      <Helmet>
        <title>{generatePageTitle("Cookie Policy")}</title>
        <meta name="description" content="How iQ Texting uses cookies and how you can manage your preferences." />
        <link rel="canonical" href={generateCanonicalUrl("/cookie-policy")} />
        <meta property="og:title" content={generatePageTitle("Cookie Policy")} />
        <meta property="og:url" content={generateCanonicalUrl("/cookie-policy")} />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container mx-auto px-4 py-6">
            <Button variant="ghost" asChild className="mb-4">
              <Link to="/">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Link>
            </Button>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Cookie Policy
            </h1>
            <p className="text-muted-foreground mt-2">Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </header>

        <main className="container mx-auto px-4 py-12 max-w-4xl">
          <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
            <p className="text-muted-foreground">
              This page is maintained by iQ Texting to explain how cookies and similar technologies are used on this site.
            </p>

            <section>
              <h2 className="text-2xl font-semibold mb-4">What are cookies?</h2>
              <p className="text-muted-foreground">
                Cookies are small text files stored on your device when you visit a website. They help the site function,
                remember your preferences, and understand how it is used.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Categories we use</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="py-2 pr-4">Category</th>
                      <th className="py-2 pr-4">Purpose</th>
                      <th className="py-2">Retention</th>
                    </tr>
                  </thead>
                  <tbody className="text-foreground">
                    <tr className="border-b">
                      <td className="py-2 pr-4 font-medium">Essential</td>
                      <td className="py-2 pr-4">Authentication, security, session management, load balancing.</td>
                      <td className="py-2">Session – 1 year</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4 font-medium">Analytics</td>
                      <td className="py-2 pr-4">Aggregated usage metrics so we can improve the product.</td>
                      <td className="py-2">Up to 13 months</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 font-medium">Marketing</td>
                      <td className="py-2 pr-4">Measuring and personalizing marketing campaigns.</td>
                      <td className="py-2">Up to 13 months</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Third parties</h2>
              <p className="text-muted-foreground">
                We rely on trusted processors to run the service. These may set cookies needed for their functions:
                Lovable Cloud (auth and database sessions), Twilio (SMS delivery), Stripe (checkout and payments),
                and any third-party site you embed our chat widget on.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Managing your preferences</h2>
              <p className="text-muted-foreground">
                You can change your choices any time using our{" "}
                <CookiePreferencesLink className="underline text-primary" />. You can also block or delete cookies in your
                browser settings, though some parts of the site may not work as expected.
              </p>
              <p className="text-muted-foreground mt-4">
                California residents may exercise the right to opt out of the sale or sharing of personal information by
                selecting "Reject non-essential" in the cookie banner or by visiting our{" "}
                <Link to="/do-not-sell" className="underline text-primary">Do Not Sell or Share My Personal Information</Link> page.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Contact</h2>
              <p className="text-muted-foreground">
                Questions about this policy? Email <a className="underline text-primary" href="mailto:privacy@iqtexting.com">privacy@iqtexting.com</a>.
              </p>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default CookiePolicy;
